import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  DollarSign,
  TrendingUp,
  Percent,
  BedDouble,
  Plus,
  ArrowRight,
  FileText,
  AlertCircle,
  Crown,
  Calendar,
  HelpCircle,
  X,
  ChevronRight,
  Sparkles,
  Calculator,
  PieChart,
  Settings,
  Lightbulb,
} from "lucide-react";
import { toast } from "sonner";

// Interactive Help Guide Component
const HelpGuide = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = [
    {
      title: "Welcome to HotelBudget! 🏨",
      icon: <Building2 className="w-8 h-8 text-emerald-600" />,
      content: (
        <div className="space-y-3">
          <p>This tool helps you create professional 5-year hotel budgets in minutes.</p>
          <p className="text-slate-600">Let's walk through how to get started...</p>
        </div>
      ),
    },
    {
      title: "Step 1: Add Your Property",
      icon: <Plus className="w-8 h-8 text-blue-600" />,
      content: (
        <div className="space-y-3">
          <p><strong>Go to Properties</strong> and click <strong>"Add Property"</strong></p>
          <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
            <li>Enter your hotel name</li>
            <li>Set the total number of rooms</li>
            <li>Optionally add a hero image</li>
          </ul>
          <p className="text-sm text-slate-500 mt-2">You can manage multiple properties from one account.</p>
        </div>
      ),
    },
    {
      title: "Step 2: Start Your Budget",
      icon: <Calculator className="w-8 h-8 text-[#4B5E40]" />,
      content: (
        <div className="space-y-3">
          <p>Click <strong>"Create Budget"</strong> on your property card.</p>
          <p className="text-slate-600">You'll see a <strong className="text-emerald-600">One-Click Budget</strong> option at the top:</p>
          <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
            <p className="text-sm"><strong>🎯 Recommended:</strong> Click "Apply Industry Standard" to instantly populate all 86 expense items with hotel industry benchmarks.</p>
          </div>
        </div>
      ),
    },
    {
      title: "Step 3: Enter Occupancy & Rates",
      icon: <BedDouble className="w-8 h-8 text-orange-600" />,
      content: (
        <div className="space-y-3">
          <p>In the <strong>Occupancy & Rates</strong> tab:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
            <li><strong>Occupancy Rate (%):</strong> Expected room occupancy per month</li>
            <li><strong>ADR ($):</strong> Average Daily Rate - what you charge per room/night</li>
          </ul>
          <p className="text-sm text-slate-500 mt-2">If you used industry standards, seasonal patterns are pre-filled.</p>
        </div>
      ),
    },
    {
      title: "Step 4: Customize Expenses",
      icon: <PieChart className="w-8 h-8 text-red-600" />,
      content: (
        <div className="space-y-3">
          <p>The <strong>Expenses</strong> tab contains 86 line items across 8 categories.</p>
          <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
            <p className="text-sm"><strong>💡 Important:</strong> Industry standards are for 50+ room hotels. Update items you know:</p>
            <ul className="list-disc list-inside text-sm text-amber-800 mt-1 ml-2">
              <li>Rent (we use 25% of turnover - enter your actual rent)</li>
              <li>Insurance premiums</li>
              <li>Staff salaries</li>
              <li>Utility costs</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      title: "Step 5: Growth & Projections",
      icon: <TrendingUp className="w-8 h-8 text-green-600" />,
      content: (
        <div className="space-y-3">
          <p>In <strong>5-Year Growth</strong>, set annual growth rates:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
            <li><strong>Occupancy Growth:</strong> How much occupancy improves each year</li>
            <li><strong>ADR Growth:</strong> Rate increases (typically 3-5%)</li>
            <li><strong>Expense Growth:</strong> Cost inflation (typically 2-3%)</li>
          </ul>
          <p className="text-sm text-slate-500 mt-2">These rates compound over 5 years in your projections.</p>
        </div>
      ),
    },
    {
      title: "Step 6: View Reports & Export",
      icon: <FileText className="w-8 h-8 text-indigo-600" />,
      content: (
        <div className="space-y-3">
          <p>Once saved, click <strong>"View Reports"</strong> to see:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
            <li>P&L Summary with charts</li>
            <li>Expense breakdowns by category</li>
            <li>Cash flow projections</li>
            <li>5-year forecasts</li>
          </ul>
          <p className="mt-2"><strong>Download Excel:</strong> Get a complete 12-sheet workbook with all 5 years of P&L and cash flow.</p>
        </div>
      ),
    },
    {
      title: "You're Ready! 🎉",
      icon: <Sparkles className="w-8 h-8 text-emerald-600" />,
      content: (
        <div className="space-y-3">
          <p>That's everything you need to create professional hotel budgets!</p>
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
            <p className="text-sm"><strong>Quick Tips:</strong></p>
            <ul className="list-disc list-inside text-sm text-slate-600 mt-1 ml-2">
              <li>Use Scenarios to model best/worst case</li>
              <li>Cash Flow settings control payment timing</li>
              <li>You can always edit and re-export</li>
            </ul>
          </div>
          <p className="text-center text-slate-500 mt-3">Click "Get Started" to begin!</p>
        </div>
      ),
    },
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg bg-white shadow-2xl">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {currentStepData.icon}
              <CardTitle className="text-lg sm:text-xl">{currentStepData.title}</CardTitle>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </Button>
          </div>
          {/* Progress indicator */}
          <div className="flex gap-1 mt-4">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  idx <= currentStep ? "bg-emerald-500" : "bg-slate-200"
                }`}
              />
            ))}
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="min-h-[200px]">
            {currentStepData.content}
          </div>
          
          <div className="flex justify-between mt-6 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setCurrentStep((s) => s - 1)}
              disabled={isFirstStep}
              className={isFirstStep ? "invisible" : ""}
            >
              Back
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose} className="text-slate-500">
                Skip
              </Button>
              {isLastStep ? (
                <Button onClick={onClose} className="bg-emerald-600 hover:bg-emerald-700">
                  Get Started
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={() => setCurrentStep((s) => s + 1)} className="bg-slate-900 hover:bg-slate-800">
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default function DashboardPage() {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHelp, setShowHelp] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDashboardStats();
    fetchSubscription();
    
    // Show help guide for first-time users
    const hasSeenHelp = localStorage.getItem("hotelbudget_help_seen");
    if (!hasSeenHelp) {
      setShowHelp(true);
    }
  }, []); // Run once on mount

  const handleCloseHelp = () => {
    setShowHelp(false);
    localStorage.setItem("hotelbudget_help_seen", "true");
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: getAuthHeader(),
      });
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
      // Error handled
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: getAuthHeader(),
      });
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to load subscription:", error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full spinner" />
        </div>
      </Layout>
    );
  }

  const kpiCards = [
    {
      title: "Total Properties",
      value: stats?.total_properties || 0,
      icon: Building2,
      color: "bg-slate-900",
      textColor: "text-slate-900",
    },
    {
      title: "Total Rooms",
      value: stats?.total_rooms || 0,
      icon: BedDouble,
      color: "bg-amber-600",
      textColor: "text-amber-700",
    },
    {
      title: "Projected Revenue",
      value: formatCurrency(stats?.projected_annual_revenue || 0),
      icon: DollarSign,
      color: "bg-emerald-600",
      textColor: "text-emerald-700",
      subtext: "Annual",
    },
    {
      title: "Avg Occupancy",
      value: `${stats?.average_occupancy || 0}%`,
      icon: Percent,
      color: "bg-blue-600",
      textColor: "text-blue-700",
    },
  ];

  return (
    <Layout>
      {/* Help Guide Modal */}
      {showHelp && <HelpGuide onClose={handleCloseHelp} />}
      
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Getting Started Help Banner */}
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <HelpCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-blue-900">
                  New to HotelBudget?
                </p>
                <p className="text-sm text-blue-700">
                  Learn how to create your first hotel budget in minutes
                </p>
              </div>
            </div>
            <Button 
              onClick={() => setShowHelp(true)} 
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white gap-2"
            >
              <Lightbulb className="w-4 h-4" />
              View Quick Guide
            </Button>
          </CardContent>
        </Card>

        {/* Subscription Status Banner */}
        {subscription && subscription.status !== "active" && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-800">
                    {subscription.status === "expired" 
                      ? "Your subscription has expired" 
                      : "No active subscription"}
                  </p>
                  <p className="text-sm text-amber-600">
                    Subscribe to create properties and budgets
                  </p>
                </div>
              </div>
              <Link to="/pricing" className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white">
                  View Plans
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {subscription && subscription.status === "active" && (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Crown className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-emerald-800">
                    {subscription.plan_name} Active
                  </p>
                  <p className="text-sm text-emerald-600 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Expires: {new Date(subscription.expires_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              {subscription.plan === "basic" && (
                <Link to="/pricing" className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full sm:w-auto border-emerald-300 text-emerald-700 hover:bg-emerald-100">
                    Upgrade to Premium
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Dashboard
            </h1>
            <p className="text-slate-500 mt-1">
              Overview of your hotel portfolio performance
            </p>
          </div>
          <Link to="/properties">
            <Button
              className="bg-slate-900 hover:bg-slate-800 btn-transition"
              data-testid="add-property-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Property
            </Button>
          </Link>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {kpiCards.map((card, index) => (
            <Card
              key={card.title}
              className="stat-card border-slate-200 animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
              data-testid={`kpi-card-${index}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">
                      {card.title}
                    </p>
                    <p
                      className={`text-2xl font-bold mt-2 ${card.textColor}`}
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {card.value}
                    </p>
                    {card.subtext && (
                      <p className="text-xs text-slate-400 mt-1">
                        {card.subtext}
                      </p>
                    )}
                  </div>
                  <div
                    className={`w-10 h-10 rounded-lg ${card.color} flex items-center justify-center`}
                  >
                    <card.icon className="w-5 h-5 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Properties Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2
              className="text-xl font-bold text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Your Properties
            </h2>
            <Link
              to="/properties"
              className="text-sm text-amber-700 hover:text-amber-800 font-medium flex items-center gap-1"
            >
              View all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {stats?.properties?.length === 0 ? (
            <Card className="border-slate-200 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                  <Building2 className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  No properties yet
                </h3>
                <p className="text-slate-500 text-center max-w-sm mb-4">
                  Get started by adding your first property to create budgets
                  and cash flow projections.
                </p>
                <Link to="/properties">
                  <Button className="bg-amber-600 hover:bg-amber-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Property
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stats?.properties?.slice(0, 6).map((property, index) => (
                <Card
                  key={property.id}
                  className="property-card border-slate-200 overflow-hidden animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`property-card-${index}`}
                >
                  <div className="h-32 bg-gradient-to-br from-slate-800 to-slate-900 relative">
                    <img
                      src="https://images.unsplash.com/photo-1765439178218-e54dcbb64bcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3RlbCUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlJTIwbmlnaHR8ZW58MHx8fHwxNzcwNjA4MTcwfDA&ixlib=rb-4.1.0&q=85"
                      alt={property.name}
                      className="w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                    <div className="absolute bottom-3 left-4 right-4">
                      <h3 className="text-lg font-bold text-white truncate">
                        {property.name}
                      </h3>
                      <p className="text-sm text-slate-300">{property.location}</p>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <BedDouble className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">
                          {property.total_rooms} rooms
                        </span>
                      </div>
                      {property.has_budget ? (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          Budget Set
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-amber-600 border-amber-300"
                        >
                          <AlertCircle className="w-3 h-3 mr-1" />
                          No Budget
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/properties/${property.id}`} className="flex-1">
                        <Button
                          variant="outline"
                          className="w-full"
                          size="sm"
                          data-testid={`view-property-${index}`}
                        >
                          View Details
                        </Button>
                      </Link>
                      {property.has_budget ? (
                        <Link to={`/properties/${property.id}`}>
                          <Button
                            size="sm"
                            className="bg-amber-600 hover:bg-amber-700"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                        </Link>
                      ) : (
                        <Link to={`/budget/${property.id}`}>
                          <Button
                            size="sm"
                            className="bg-slate-900 hover:bg-slate-800"
                            data-testid={`create-budget-${index}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        {stats?.properties?.length > 0 && (
          <Card className="border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3
                    className="text-xl font-bold text-white"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    Ready to create a budget?
                  </h3>
                  <p className="text-slate-300 mt-1">
                    Generate comprehensive 5-year projections for your properties
                  </p>
                </div>
                <Link to="/properties">
                  <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
